/**
 * Os padrões que entraram na comparação (E67.F1.T03).
 *
 * A prancha mostra um padrão por vez — são duas imagens lado a lado, e empilhar
 * M delas destruiria a leitura que a comparação existe para permitir. O que a
 * Fase corrige é a **mentira por omissão**: até aqui as telas ilustravam com o
 * primeiro padrão sem dizer que o motor usou todos (`pre_analise_service` monta
 * `padroes_imgs` com o conjunto inteiro). Agora o rodapé declara quantos
 * entraram e quem escolhe qual olhar é quem lê.
 *
 * Com um único padrão o seletor não aparece: não há o que escolher.
 *
 * Compartilhado entre a tela do perito e a do cliente de propósito — o número
 * de padrões que a comparação usou é o mesmo fato dos dois lados do balcão, e
 * duas implementações dele divergiriam no plural.
 */
import { useId } from 'react';

import { ptBR } from '@/i18n/pt-BR';
import type { SignatureRecordData } from '@/lib/case_signatures';

export interface PadroesUsadosProps {
  padroes: SignatureRecordData[];
  exibidoId: string | null;
  onExibir: (id: string) => void;
}

export function PadroesUsados({
  padroes,
  exibidoId,
  onExibir,
}: PadroesUsadosProps): JSX.Element | null {
  // E67.F1.C02 (F-E67.F1-02): a identidade do grupo é da INSTÂNCIA, não do
  // conteúdo. Ancorá-la no primeiro padrão parecia estável — e era —, mas todas
  // as questionadas do caso recebem o MESMO conjunto de padrões, então as N
  // instâncias nasciam com o mesmo `name` e o navegador as tratava como um
  // grupo de rádio só: escolher o padrão da segunda comparação desmarcava o da
  // primeira. `useId` dá um valor estável entre renders e único por instância.
  const instancia = useId();
  const grupo = `padrao-exibido-${instancia}`;
  if (padroes.length === 0) return null;
  const resumo =
    padroes.length === 1
      ? ptBR.preAnalise.comparacaoUmPadraoUsado
      : ptBR.preAnalise.comparacaoPadroesUsados.replace('{n}', String(padroes.length));

  return (
    <div className="mt-sc-3" data-testid="pre-analise-padroes-usados">
      <p className="text-caption text-fg-muted" data-testid="pre-analise-padroes-resumo">
        {resumo}
      </p>
      {padroes.length > 1 && (
        <fieldset className="mt-sc-2 flex flex-wrap items-center gap-sc-2 border-0 p-0">
          <legend className="sr-only">{ptBR.preAnalise.padraoExibidoLabel}</legend>
          {padroes.map((padrao, indice) => {
            const rotulo = ptBR.preAnalise.padraoOrdinal.replace('{n}', String(indice + 1));
            const selecionado = padrao.id === exibidoId;
            return (
              <label
                key={padrao.id}
                className={`cursor-pointer border px-sc-2 py-1 text-caption ${
                  selecionado
                    ? 'border-accent bg-accent-bg text-accent-800'
                    : 'border-border text-fg-muted hover:bg-surface-2'
                }`}
                data-testid="pre-analise-padrao-opcao"
              >
                <input
                  type="radio"
                  className="sr-only"
                  name={grupo}
                  checked={selecionado}
                  onChange={() => onExibir(padrao.id)}
                />
                {rotulo}
              </label>
            );
          })}
        </fieldset>
      )}
    </div>
  );
}
